import React, { useState, useEffect } from "react";
import { Card, Form, Button, Spinner } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { toast } from "react-toastify";
import useUserAgreements from "hooks/useUserAgreements";

// Import the Rich Text Editor
import RichTextEditor from "../../common/RichTextEditor";

// ---------------------------------------------
// User Agreement Form Component
// ---------------------------------------------
const UserAgreementForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const { getAgreementById, addAgreement, updateAgreement } =
    useUserAgreements();

  const [formData, setFormData] = useState({
    version: "",
    details: "",
  });

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ------------------------------------------------
  // Load existing agreement for edit mode
  // ------------------------------------------------
  useEffect(() => {
    if (!isEditMode) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const data = await getAgreementById(id);
        setFormData({
          version: data.version,
          details: data.details,
        });
      } catch (err) {
        toast.error("Failed to load agreement: " + err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, isEditMode, getAgreementById]);

  // ------------------------------------------------
  // Update form fields
  // ------------------------------------------------
  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ------------------------------------------------
  // Validate Quill Empty Content
  // ------------------------------------------------
  const isQuillEmpty = (html) => {
    if (!html) return true;
    const cleaned = html.replace(/<(.|\n)*?>/g, "").trim(); // strip html tags
    return cleaned.length === 0;
  };

  // ------------------------------------------------
  // Submit Form
  // ------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.version.trim()) {
      toast.error("Version cannot be empty.");
      return;
    }

    if (isQuillEmpty(formData.details)) {
      toast.error("Agreement Content cannot be blank.");
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        await updateAgreement(id, formData);
        toast.success(`Agreement ${formData.version} updated successfully.`);
      } else {
        await addAgreement(formData);
        toast.success(`Agreement ${formData.version} created successfully.`);
      }
      // Navigate back to list after either create or edit
      navigate(-1);
    } catch (err) {
      toast.error("Operation failed: " + (err.message || "Unknown error."));
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = isEditMode
    ? `Edit Agreement: ${formData.version || "Loading..."}`
    : "Create New Agreement";

  const subtitle = isEditMode
    ? "Modify the version or update the legal document content."
    : "Draft a new user agreement version.";

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3">
        <WidgetSectionTitle
          icon="file-contract"
          title={title}
          subtitle={subtitle}
          className="mb-3 mt-3 flex-1"
        />

        <Button
          variant="info"
          size="sm"
          className="text-sm"
          onClick={() => navigate("/general/user-agreements")}
        >
          Go Back
        </Button>
      </div>

      <Card className="custom-shadow">
        <Card.Body>
          {loading && isEditMode ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Form onSubmit={handleSubmit}>
              {/* Version Field */}
              <Form.Group className="mb-4" controlId="agreementVersion">
                <Form.Label>Agreement Version</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.version}
                  placeholder="Enter version (e.g., v1.0 or 2025-11-01)"
                  onChange={(e) => handleChange("version", e.target.value)}
                  required
                  autoComplete="off"
                />
              </Form.Group>

              {/* Rich Text Editor */}
              <RichTextEditor
                value={formData.details}
                onChange={(html) => handleChange("details", html)}
              />

              {/* Footer Buttons */}
              <div className="d-flex justify-content-end mt-7">
                <Button
                  variant="success"
                  type="submit"
                  size="sm"
                  disabled={isSubmitting || loading}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {isEditMode ? "Saving..." : "Creating..."}
                    </>
                  ) : (
                    isEditMode ? "Save Changes" : "Create Agreement"
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default UserAgreementForm;
